import { motion } from "framer-motion";
import { Search, CalendarCheck, TrendingUp, CheckCircle2 } from "lucide-react";
import LeadForm from "@/components/LeadForm";
import useSeo from "@/lib/useSeo";

export default function Book() {
  useSeo({
    title: "Book a Free Dental RCM Audit | ClariDent RCM",
    description: "Claim your free, no-obligation dental RCM audit. We review your claims, denials, and AR aging and show you exactly what your practice could recover.",
    path: "/book",
  });
  return (
    <main data-testid="book-page">
      <section className="bg-navy-950 py-20 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,143,143,0.2),transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-400 mb-4">Book a Consultation</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black max-w-3xl" data-testid="book-heading">
            Claim your <span className="text-teal-400">free RCM audit.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
            No obligation. No commitment. Just insights that add value. We'll review your claims, denials, and AR — and show you exactly what your practice could recover.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h2 className="font-display text-3xl font-bold text-navy-900 mb-10">What happens next</h2>
          <div className="space-y-8">
            {[
              { icon: CalendarCheck, t: "You book", d: "Fill in the form and tell us a little about your practice. We respond within one business day." },
              { icon: Search, t: "We audit", d: "On a short call, we review your claims workflow, denial patterns, and AR aging — then deliver a written summary of what's leaking and why." },
              { icon: TrendingUp, t: "You decide", d: "Keep the insights and walk away, or let us fix it. Either way, the audit is yours to keep. Free means free." },
            ].map((s, i) => (
              <div key={s.t} className="flex gap-5" data-testid={`book-step-${i}`}>
                <span className="h-12 w-12 shrink-0 rounded-xl bg-teal-50 flex items-center justify-center">
                  <s.icon size={22} className="text-teal-600" />
                </span>
                <div>
                  <h3 className="font-display text-xl font-semibold text-navy-900">{s.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 rounded-2xl bg-slate-50 border border-slate-200 p-7">
            <p className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">The audit reviews</p>
            <ul className="space-y-3 text-sm text-slate-600">
              {["Claim submission speed and first-pass acceptance", "Denial causes and appeal opportunities", "AR aging by bucket (0–30, 31–60, 61–90, 90+ days)", "Patient billing and statement workflow", "How much you'd save at our rates (from 1.80%) vs. your current setup"].map((f) => (
                <li key={f} className="flex items-start gap-2"><CheckCircle2 size={17} className="text-teal-600 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
          <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 shadow-xl" data-testid="book-form-card">
            <h3 className="font-display text-2xl font-bold text-navy-900 mb-6">Book your free audit</h3>
            <LeadForm type="consultation" testIdPrefix="book-form" />
          </div>
        </motion.div>
      </section>
    </main>
  );
}
